import { FEATURE_FLAGS, type FeatureFlagKey, type FeatureFlags, flagEnabled } from "../hooks/useAuth";

export type AdminTab = {
  to: string;
  label: string;
  // Hides the tab entirely when the flag is off.
  featureFlag?: FeatureFlagKey;
  // Tab stays visible when off, but its page is gated (shows a notice) and its
  // default view isn't prefetched (the endpoints 404 while off).
  dataFlag?: FeatureFlagKey;
};

export type AdminNavGroup = { label: string; tabs: ReadonlyArray<AdminTab> };

// Grouped by the job an admin is doing; route segments are the URLs.
export const ADMIN_NAV: ReadonlyArray<AdminNavGroup> = [
  {
    label: "Customers",
    tabs: [
      { to: "instances", label: "Instances" },
      { to: "users", label: "Users" },
      { to: "invites", label: "Invites" },
    ],
  },
  {
    label: "Support",
    tabs: [
      { to: "support", label: "Support" },
      { to: "announcements", label: "Announcements" },
      { to: "secure-notes", label: "Secure notes" },
    ],
  },
  {
    label: "Tool calls",
    tabs: [
      { to: "tool-usage", label: "Usage" },
      { to: "tool-calls", label: "Logs" },
      { to: "rate-limits", label: "Rate limits" },
    ],
  },
  {
    label: "Billing",
    tabs: [
      { to: "billing", label: "Subscriptions", dataFlag: FEATURE_FLAGS.billingStripe },
      { to: "tiers", label: "Tiers & pricing", dataFlag: FEATURE_FLAGS.billingStripe },
    ],
  },
  {
    label: "Platform",
    tabs: [
      { to: "integrations", label: "Integrations" },
      { to: "plugins", label: "Plugins" },
      { to: "ai-models", label: "AI models", featureFlag: FEATURE_FLAGS.aiConfig },
      { to: "feature-flags", label: "Feature flags" },
      { to: "resources", label: "Resources" },
    ],
  },
  {
    label: "Activity",
    tabs: [
      { to: "audit", label: "Audit" },
      { to: "events", label: "Events" },
    ],
  },
];

export const visibleAdminNav = (features: FeatureFlags | undefined): ReadonlyArray<AdminNavGroup> =>
  ADMIN_NAV.map((group) => ({
    ...group,
    tabs: group.tabs.filter((tab) => !tab.featureFlag || flagEnabled(features, tab.featureFlag)),
  })).filter((group) => group.tabs.length > 0);

export const unreviewedBadge = (count: number): string | null => (count <= 0 ? null : count > 99 ? "99+" : String(count));
